import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from './useDebounce';

export interface PaginatedSelectOption {
  value: string;
  label: string;
}

/** Server envelope every paginated endpoint returns. */
export interface PaginatedSelectResponse<TItem> {
  data: TItem[];
  meta?: { totalCount?: number } | null;
}

export interface UsePaginatedSelectParams<TItem> {
  /**
   * Base query key. The debounced search term and page size are appended
   * internally — pages themselves are managed by the infinite query.
   */
  queryKey: unknown[];
  /** Fetches one 0-based page for the given (already-debounced) search term. */
  queryFn: (params: {
    page: number;
    count: number;
    search: string;
  }) => Promise<PaginatedSelectResponse<TItem>>;
  /** Maps a raw item to its option value + label. */
  getOption: (item: TItem) => PaginatedSelectOption;
  /** Gate fetching (e.g. pass the dropdown's open state to avoid eager fetch). Default: true. */
  enabled?: boolean;
  /** Items per page. Default: 20. */
  pageSize?: number;
  /** Search debounce in ms. Default: 300. */
  debounceMs?: number;
  /** Passed through to the query — how long results stay fresh. Default: RQ default (0). */
  staleTime?: number;
}

export interface UsePaginatedSelectResult<TItem> {
  /** Options flattened across all loaded pages. */
  options: PaginatedSelectOption[];
  /** Raw items flattened across all loaded pages (for callers that need item metadata). */
  items: TItem[];
  isLoading: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  /** Current (raw, un-debounced) search keyword. */
  search: string;
  onSearchChange: (search: string) => void;
}

/**
 * Generic logic for a single-select dropdown backed by a server-paginated,
 * title-searched endpoint: debounced search, "load more" pagination and page
 * accumulation. Built on TanStack `useInfiniteQuery` + `useDebounce`.
 *
 * The caller owns the data source (`queryFn`) and the value/label mapping
 * (`getOption`); this hook owns search state, debouncing and pagination.
 * Changing the debounced search resets to page 0 (a new query key).
 */
export function usePaginatedSelect<TItem>({
  queryKey,
  queryFn,
  getOption,
  enabled = true,
  pageSize = 20,
  debounceMs = 300,
  staleTime,
}: UsePaginatedSelectParams<TItem>): UsePaginatedSelectResult<TItem> {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, debounceMs);

  const query = useInfiniteQuery({
    queryKey: [...queryKey, debouncedSearch, pageSize],
    queryFn: ({ pageParam }) =>
      queryFn({ page: pageParam, count: pageSize, search: debouncedSearch }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < (lastPage.meta?.totalCount ?? 0)
        ? pages.length
        : undefined;
    },
    enabled,
    ...(staleTime !== undefined ? { staleTime } : {}),
  });

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  const items = useMemo(
    () => data?.pages.flatMap(page => page.data) ?? [],
    [data],
  );
  const options = useMemo(() => items.map(getOption), [items, getOption]);

  const onLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    options,
    items,
    isLoading: query.isLoading,
    hasMore: !!hasNextPage,
    isFetchingMore: isFetchingNextPage,
    onLoadMore,
    search,
    onSearchChange: setSearch,
  };
}
